import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

export interface RetryConfig {
  retries?: number;
  retryDelay?: number;
  retryCondition?: (error: AxiosError) => boolean;
  onRetry?: (retryCount: number, error: AxiosError) => void;
  exponentialBackoff?: boolean;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  retries: 3,
  retryDelay: 1000,
  exponentialBackoff: true,
  retryCondition: (error: AxiosError) => {
    // Retry on network errors and 5xx server errors
    return !error.response || (error.response.status >= 500 && error.response.status < 600);
  }
};

/**
 * Add retry logic to axios instance
 */
export function axiosRetry(instance: any, customConfig?: RetryConfig) {
  const config = { ...DEFAULT_RETRY_CONFIG, ...customConfig };

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const { config: requestConfig } = error;
      
      if (!requestConfig) {
        return Promise.reject(error);
      }

      // Initialize retry count
      const currentRetryCount = (requestConfig as any).__retryCount || 0;
      
      // Check if we should retry
      if (
        currentRetryCount >= (config.retries || 0) ||
        (config.retryCondition && !config.retryCondition(error))
      ) {
        return Promise.reject(error);
      }

      // Increment retry count
      (requestConfig as any).__retryCount = currentRetryCount + 1;

      // Calculate delay
      const delay = config.exponentialBackoff
        ? config.retryDelay! * Math.pow(2, currentRetryCount)
        : config.retryDelay!;

      // Call onRetry callback if provided
      if (config.onRetry) {
        config.onRetry(currentRetryCount + 1, error);
      }

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));

      // Retry the request
      return instance.request(requestConfig);
    }
  );
}

/**
 * Wrapper for making requests with retry logic
 */
export async function requestWithRetry<T>(
  config: AxiosRequestConfig,
  retryConfig?: RetryConfig
): Promise<T> {
  const mergedRetryConfig = { ...DEFAULT_RETRY_CONFIG, ...retryConfig };
  let lastError: AxiosError | null = null;

  for (let attempt = 0; attempt <= (mergedRetryConfig.retries || 0); attempt++) {
    try {
      const response = await axios.request<T>(config);
      return response.data;
    } catch (error) {
      lastError = error as AxiosError;

      // Check if we should retry
      if (
        attempt >= (mergedRetryConfig.retries || 0) ||
        (mergedRetryConfig.retryCondition && !mergedRetryConfig.retryCondition(lastError))
      ) {
        throw lastError;
      }

      // Calculate delay
      const delay = mergedRetryConfig.exponentialBackoff
        ? mergedRetryConfig.retryDelay! * Math.pow(2, attempt)
        : mergedRetryConfig.retryDelay!;

      // Call onRetry callback
      if (mergedRetryConfig.onRetry) {
        mergedRetryConfig.onRetry(attempt + 1, lastError);
      }

      // Wait before next attempt
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

/**
 * Circuit breaker implementation for handling repeated failures
 */
export class CircuitBreaker {
  private failureCount = 0;
  private lastFailureTime = 0;
  private state: 'closed' | 'open' | 'half-open' = 'closed';

  constructor(
    private readonly threshold: number = 5,
    private readonly timeout: number = 60000 // 1 minute
  ) {}

  async execute<T>(fn: () => Promise<T>): Promise<T> {
    if (this.state === 'open') {
      if (Date.now() - this.lastFailureTime > this.timeout) {
        this.state = 'half-open';
      } else {
        throw new Error('Circuit breaker is open');
      }
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  private onSuccess() {
    this.failureCount = 0;
    this.state = 'closed';
  }

  private onFailure() {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    if (this.failureCount >= this.threshold) {
      this.state = 'open';
    }
  }

  getState() {
    return {
      state: this.state,
      failureCount: this.failureCount,
      lastFailureTime: this.lastFailureTime
    };
  }

  reset() {
    this.failureCount = 0;
    this.lastFailureTime = 0;
    this.state = 'closed';
  }
}